import * as THREE from 'three/webgpu';
import { WebGPUAtmosphere } from '../atmosphere/WebGPUAtmosphere.js';
import { WebGPUCloudVolume } from '../clouds/WebGPUCloudVolume.js';
import { WebGPUCloudShadowMap } from '../clouds/WebGPUCloudShadowMap.js';
import { WebGPUCloudRenderPipeline } from '../clouds/WebGPUCloudRenderPipeline.js';
import { AdaptiveQualityController } from '../clouds/AdaptiveQualityController.js';
import { CLOUD_STYLE_PRESETS } from '../clouds/CloudStyles.js';
import { CloudControlPanel } from './CloudControlPanel.js';
import { createDefaultsSnapshot, mergeDefaultSections, normalizeDefaults } from './DefaultsStore.js';
import { FlightControls } from './FlightControls.js';
import { createWorld } from './createWorld.js';
import PROJECT_DEFAULTS_SOURCE from '../../config/cloud-defaults.json';
import './styles.css';

const app = document.querySelector( '#app' );
const query = new URLSearchParams( window.location.search );
const SAVED_DEFAULTS_KEY = 'cyborg-clouds:defaults:v1';
const PROJECT_DEFAULTS_ENDPOINT = '/__cyborg-clouds/project-defaults';
const PROJECT_DEFAULTS = normalizeDefaults( PROJECT_DEFAULTS_SOURCE );
window.__CYBORG_STATUS__ = { stage: 'module-loaded', startedAt: performance.now() };

function loadSavedDefaults() {

	if ( query.get( 'saved' ) === '0' ) return null;
	try {

		const parsed = JSON.parse( window.localStorage.getItem( SAVED_DEFAULTS_KEY ) );
		return parsed ? normalizeDefaults( parsed ) : null;

	} catch {

		return null;

	}

}

function saveDefaults( defaults ) {

	try {

		window.localStorage.setItem( SAVED_DEFAULTS_KEY, JSON.stringify( normalizeDefaults( { version: 1, ...defaults } ) ) );
		return true;

	} catch {

		return false;

	}

}

async function writeProjectDefaults( defaults = null ) {

	try {

		const response = await fetch( PROJECT_DEFAULTS_ENDPOINT, {
			method: defaults ? 'POST' : 'DELETE',
			headers: defaults ? { 'Content-Type': 'application/json' } : undefined,
			body: defaults ? JSON.stringify( normalizeDefaults( defaults ) ) : undefined
		} );
		if ( ! response.ok ) throw new Error( ( await response.json() ).error ?? `HTTP ${ response.status }` );
		return true;

	} catch ( error ) {

		console.error( 'Could not update project defaults.', error );
		return false;

	}

}

async function promoteProjectDefaults( defaults = null ) {

	const written = await writeProjectDefaults( defaults );
	if ( ! written ) return false;
	return clearSavedDefaults();

}

function clearSavedDefaults() {

	try {

		window.localStorage.removeItem( SAVED_DEFAULTS_KEY );
		return true;

	} catch {

		return false;

	}

}

function markStage( stage ) {

	window.__CYBORG_STATUS__.stage = stage;
	window.__CYBORG_STATUS__[ stage ] = performance.now() - window.__CYBORG_STATUS__.startedAt;
	document.documentElement.dataset.cloudStage = stage;

}

function showError( error ) {

	console.error( error );
	document.body.insertAdjacentHTML( 'beforeend', `<section class="error-card"><div><span>WEBGPU STARTUP FAILED</span><h1>CyborgClouds could not start.</h1><p>Use a current WebGPU-capable browser with hardware acceleration enabled.</p><pre>${ String( error?.stack ?? error ) }</pre></div></section>` );

}

async function start() {

	if ( ! navigator.gpu ) throw new Error( 'navigator.gpu is unavailable.' );
	markStage( 'webgpu-available' );

	document.body.insertAdjacentHTML( 'beforeend', `
		<section class="loading-screen"><div class="loading-box"><span>WEBGPU COMPUTE</span><h2>Building cloud and atmosphere fields</h2><div class="loading-track"></div></div></section>
		<section class="brand-card"><span class="kicker">PHYSICAL WEBGPU ATMOSPHERE</span><h2>Fly the weather.</h2><p>Compute-built sky radiance, automatic solar color, aerial perspective, and ray-marched volumetric clouds.</p><div class="flight-hint">DRAG TO LOOK · WASD MOVE · Q/E ALTITUDE · SHIFT BOOST</div></section>
	` );

	const renderer = new THREE.WebGPURenderer( { antialias: true, powerPreference: 'high-performance' } );
	renderer.outputColorSpace = THREE.SRGBColorSpace;
	renderer.toneMapping = THREE.ACESFilmicToneMapping;
	renderer.toneMappingExposure = 1.08;
	renderer.setSize( window.innerWidth, window.innerHeight );
	app.appendChild( renderer.domElement );

	markStage( 'renderer-created' );
	await renderer.init();
	markStage( 'renderer-initialized' );

	if ( renderer.backend?.isWebGPUBackend !== true ) throw new Error( 'Three.js selected a non-WebGPU backend.' );
	document.documentElement.dataset.rendererBackend = 'webgpu';

	const scene = new THREE.Scene();
	const cloudScene = new THREE.Scene();
	const camera = new THREE.PerspectiveCamera( 58, window.innerWidth / window.innerHeight, 0.5, 6000 );
	const controls = new FlightControls( camera, renderer.domElement );
	controls.setShot( query.get( 'shot' ) ?? 'exterior' );
	const validationWorldX = Number( query.get( 'worldX' ) );
	const validationWorldZ = Number( query.get( 'worldZ' ) );
	if ( Number.isFinite( validationWorldX ) ) camera.position.x += validationWorldX;
	if ( Number.isFinite( validationWorldZ ) ) camera.position.z += validationWorldZ;
	const motionTest = query.get( 'motionTest' ) === '1';
	const motionTarget = new THREE.Vector3( - 45, 182, - 40 );
	motionTarget.x += Number.isFinite( validationWorldX ) ? validationWorldX : 0;
	motionTarget.z += Number.isFinite( validationWorldZ ) ? validationWorldZ : 0;
	let motionTestTime = 0;
	if ( motionTest ) controls.enabled = false;

	const savedDefaults = loadSavedDefaults();
	const requestedQuality = query.get( 'quality' ) ?? savedDefaults?.cloud?.quality ?? PROJECT_DEFAULTS.cloud?.quality ?? 'balanced';
	const requestedStyle = query.get( 'style' ) ?? 'cumulus';
	const legacySunset = requestedStyle === 'sunset';
	const atmosphereParameters = {
		...mergeDefaultSections( PROJECT_DEFAULTS, savedDefaults, 'atmosphere' ),
		quality: requestedQuality,
		sunElevation: savedDefaults?.atmosphere?.sunElevation
			?? PROJECT_DEFAULTS.atmosphere?.sunElevation
			?? ( legacySunset ? 0 : 18 )
	};
	for ( const key of [
		'sunAzimuth', 'sunElevation', 'sunsetStartElevation', 'sunsetWarmth',
		'sunsetBrightness', 'turbidity', 'rayleigh', 'mieCoefficient',
		'mieDirectionalG', 'ozone', 'multipleScattering', 'skyIntensity',
		'aerialDensity', 'exposure', 'timeScale'
	] ) {

		if ( query.has( key ) ) atmosphereParameters[ key ] = Number( query.get( key ) );

	}
	for ( const key of [ 'automaticLighting', 'autoExposure', 'animateTime' ] ) {

		if ( query.has( key ) ) atmosphereParameters[ key ] = query.get( key ) !== '0';

	}
	if ( query.has( 'groundAlbedo' ) ) atmosphereParameters.groundAlbedo = query.get( 'groundAlbedo' );
	const atmosphere = new WebGPUAtmosphere( renderer, { quality: requestedQuality, parameters: atmosphereParameters } );
	scene.add( atmosphere );
	window.__CYBORG_STATUS__.atmosphere = atmosphere;

	const cloudParameters = {
		...mergeDefaultSections( PROJECT_DEFAULTS, savedDefaults, 'cloud' ),
		quality: requestedQuality,
		frozen: query.has( 'freeze' )
			? query.get( 'freeze' ) === '1'
			: Boolean( savedDefaults?.cloud?.frozen ?? PROJECT_DEFAULTS.cloud?.frozen )
	};
	const cloudStyle = legacySunset ? 'towering' : requestedStyle;
	if ( CLOUD_STYLE_PRESETS[ cloudStyle ] ) {

		const { label, ...styleParameters } = CLOUD_STYLE_PRESETS[ cloudStyle ];
		Object.assign(
			cloudParameters,
			styleParameters,
			PROJECT_DEFAULTS.cloud ?? {},
			savedDefaults?.cloud ?? {},
			{ quality: requestedQuality }
		);

	}
	for ( const key of [
		'coverage', 'density', 'shapeScale', 'worldScale', 'weatherScale', 'weatherStrength',
		'warpScale', 'warpStrength', 'baseBlend', 'detailBlend',
		'detailErosion', 'detailContrast', 'detailScale',
		'formationStrength', 'formationSpeed', 'roilStrength', 'roilSpeed',
		'windDirection', 'windSpeed', 'turbulenceSpeed',
		'extinction', 'sunIntensity', 'ambientIntensity', 'sunAzimuth', 'sunElevation',
		'debugMode', 'jitterStrength', 'earlyExit', 'viewSteps', 'lightSteps',
		'detailOctaves', 'renderScale', 'detailFilter', 'lightDistance',
		'shadowDistribution'
	] ) {

		if ( query.has( key ) ) cloudParameters[ key ] = Number( query.get( key ) );

	}
	const resourceDefaults = mergeDefaultSections( PROJECT_DEFAULTS, savedDefaults, 'resources' );
	const queryNumber = ( key, fallback ) => query.has( key ) ? Number( query.get( key ) ) : fallback;
	const clouds = new WebGPUCloudVolume( renderer, {
		size: [ 8000, 220, 8000 ],
		position: [ 0, 180, 0 ],
		baseResolution: queryNumber( 'base', resourceDefaults.baseResolution ?? 96 ),
		detailResolution: queryNumber( 'detail', resourceDefaults.detailResolution ?? 48 ),
		seed: queryNumber( 'seed', resourceDefaults.seed ?? 11 ),
		parameters: cloudParameters
	} );
	clouds.visible = query.get( 'noclouds' ) !== '1';
	window.__CYBORG_STATUS__.clouds = clouds;
	markStage( 'cloud-object-created' );
	cloudScene.add( clouds );

	const shadowParameters = mergeDefaultSections( PROJECT_DEFAULTS, savedDefaults, 'cloudShadows' );
	const legacyTimedShadows = Object.hasOwn( shadowParameters, 'updateRate' ) || Object.hasOwn( shadowParameters, 'smoothing' );
	if ( legacyTimedShadows ) {

		if ( shadowParameters.resolution === 512 ) shadowParameters.resolution = 256;
		if ( shadowParameters.samples === 12 ) shadowParameters.samples = 8;
		if ( shadowParameters.strength === 0.72 ) shadowParameters.strength = 0.62;
		if ( shadowParameters.softness === 1.5 ) shadowParameters.softness = 2.5;
		delete shadowParameters.updateRate;
		delete shadowParameters.smoothing;

	}
	for ( const key of [ 'resolution', 'distance', 'samples', 'strength', 'softness', 'density', 'minimumLight', 'gamma' ] ) {

		if ( query.has( `shadow${ key[ 0 ].toUpperCase() }${ key.slice( 1 ) }` ) ) {

			shadowParameters[ key ] = Number( query.get( `shadow${ key[ 0 ].toUpperCase() }${ key.slice( 1 ) }` ) );

		}

	}
	if ( query.has( 'cloudShadows' ) ) shadowParameters.enabled = query.get( 'cloudShadows' ) !== '0';
	const cloudShadows = new WebGPUCloudShadowMap( renderer, clouds, shadowParameters );
	const world = createWorld( cloudShadows );
	scene.add( world );

	const sunLight = new THREE.DirectionalLight( 0xffffff, 3.2 );
	scene.add( sunLight );
	const hemisphereLight = new THREE.HemisphereLight( 0xaad9ed, 0x314541, 1.8 );
	scene.add( hemisphereLight );

	markStage( 'compute-started' );
	await clouds.initialize();
	await atmosphere.initialize();
	await cloudShadows.initialize( camera );
	document.documentElement.dataset.cloudReady = String( clouds.initialized );
	document.documentElement.dataset.atmosphereReady = 'true';
	markStage( 'compute-finished' );

	const savedTemporal = mergeDefaultSections( PROJECT_DEFAULTS, savedDefaults, 'temporal' );
	const cloudPipeline = new WebGPUCloudRenderPipeline( renderer, {
		scene,
		cloudScene,
		camera,
		cloudVolume: clouds,
		cloudScale: clouds.parameters.renderScale,
		sceneSamples: 4,
		aerialDensity: atmosphere.parameters.aerialDensity,
		temporalEnabled: query.has( 'temporal' ) ? query.get( 'temporal' ) !== '0' : savedTemporal.enabled,
		historyWeight: query.has( 'historyWeight' ) ? Number( query.get( 'historyWeight' ) ) : savedTemporal.historyWeight,
		temporalDepthThreshold: savedTemporal.depthThreshold,
		temporalAlphaThreshold: savedTemporal.alphaThreshold,
		motionRejection: savedTemporal.motionRejection,
		depthSharpness: savedTemporal.depthSharpness,
		alphaSharpness: savedTemporal.alphaSharpness,
		clampExpansion: savedTemporal.clampExpansion,
		detailBlur: query.has( 'detailBlur' ) ? Number( query.get( 'detailBlur' ) ) : savedTemporal.detailBlur,
		occlusionSoftness: savedTemporal.occlusionSoftness
	} );
	document.documentElement.dataset.cloudPipeline = 'linear-depth-temporal';

	const adaptive = new AdaptiveQualityController( {
		quality: clouds.parameters.quality,
		targetFrameMs: 16.7,
		enabled: query.has( 'adaptive' )
			? query.get( 'adaptive' ) === '1'
			: Boolean( savedDefaults?.adaptive?.enabled ?? PROJECT_DEFAULTS.adaptive?.enabled )
	} );

	const resizeRenderer = ( renderScale = clouds.parameters.renderScale ) => {

		const requestedPixelRatio = Number( query.get( 'dpr' ) ) || window.devicePixelRatio;
		renderer.setPixelRatio( Math.min( requestedPixelRatio, 1.5 ) );
		renderer.setSize( window.innerWidth, window.innerHeight, false );
		cloudPipeline.setCloudScale( renderScale );

	};

	resizeRenderer();

	const getCurrentDefaults = () => createDefaultsSnapshot( {
		cloud: { ...clouds.parameters },
		atmosphere: { ...atmosphere.parameters },
		temporal: { ...cloudPipeline.getTemporalOptions() },
		cloudShadows: { ...cloudShadows.parameters },
		adaptive: { enabled: adaptive.enabled },
		resources: {
			seed: clouds.resources.seed,
			baseResolution: clouds.resources.baseResolution,
			detailResolution: clouds.resources.detailResolution
		}
	} );

	const panel = new CloudControlPanel( {
		clouds,
		atmosphere,
		adaptive,
		cloudPipeline,
		cloudShadows,
		onRenderScale: resizeRenderer,
		onQuality: () => {

			resizeRenderer();
			cloudPipeline.resetTemporal();
			lastLighting = null;

		},
		onAtmosphereChange: () => {

			lastLighting = null;
			syncEnvironment( true );

		},
		onSaveDefaults: () => saveDefaults( getCurrentDefaults() ),
		onClearDefaults: clearSavedDefaults,
		onSaveProjectDefaults: () => promoteProjectDefaults( getCurrentDefaults() ),
		onResetProjectDefaults: () => promoteProjectDefaults(),
		onExportDefaults: getCurrentDefaults,
		onImportDefaults: ( defaults ) => saveDefaults( normalizeDefaults( defaults ) ),
		onShot: ( shot ) => {

			controls.setShot( shot );
			cloudPipeline.resetTemporal();

		}
	} );
	panel.element.querySelector( '[data-adaptive]' ).checked = adaptive.enabled;
	panel.setComputeStats( clouds.getStats() );
	panel.setAtmosphereStats( atmosphere.getStats() );

	if ( query.get( 'ui' ) === '0' ) {

		panel.element.style.display = 'none';
		panel.shotDock.style.display = 'none';
		document.querySelector( '.brand-card' ).style.display = 'none';

	}

	let previousFrameTime = performance.now();
	let metricTimer = 0;
	let smoothFrameMs = 16.7;
	let lastLighting = null;
	let lastAutomaticLighting = null;

	function syncEnvironment( force = false ) {

		const automatic = atmosphere.parameters.automaticLighting;
		const lighting = atmosphere.getLighting();
		if ( ! force && lighting === lastLighting && automatic === lastAutomaticLighting ) return;

		if ( automatic ) {

			atmosphere.applyLighting( { clouds, sunLight, hemisphereLight, pipeline: cloudPipeline, renderer } );

		} else {

			clouds.setSunDirection( null );
			clouds.setEnvironmentLighting( null );
			const direction = clouds.cloudUniforms.sunDirection.value;
			sunLight.position.copy( direction ).multiplyScalar( 500 );
			sunLight.color.copy( clouds.cloudUniforms.sunColor.value );
			sunLight.intensity = clouds.parameters.sunIntensity * 0.58;
			hemisphereLight.color.set( clouds.parameters.ambientTop );
			hemisphereLight.groundColor.set( clouds.parameters.ambientBottom );
			hemisphereLight.intensity = 1.8;
			cloudPipeline.setAerialPerspective( { enabled: false } );
			renderer.toneMappingExposure = atmosphere.parameters.exposure;

		}

		lastLighting = lighting;
		lastAutomaticLighting = automatic;

	}

	syncEnvironment( true );

	renderer.setAnimationLoop( () => {

		const frameTime = performance.now();
		const delta = Math.min( ( frameTime - previousFrameTime ) / 1000, 0.1 );
		previousFrameTime = frameTime;
		if ( motionTest ) {

			motionTestTime += delta;
			camera.position.set(
				80 + Math.sin( motionTestTime * 0.72 ) * 105,
				108 + Math.sin( motionTestTime * 0.41 ) * 24,
				680 - motionTestTime * 46
			);
			camera.lookAt( motionTarget );

		}
		controls.update( delta );
		world.updateForCamera( camera );
		clouds.followCamera( camera, 32 );
		clouds.update( delta );
		cloudShadows.update( camera, delta );
		atmosphere.update( camera, delta );
		syncEnvironment();

		const frameMs = delta * 1000;
		smoothFrameMs += ( frameMs - smoothFrameMs ) * 0.06;
		const qualityChange = adaptive.sample( frameMs );

		if ( qualityChange ) {

			clouds.setQuality( qualityChange );
			atmosphere.setQuality( qualityChange );
			cloudPipeline.resetTemporal();
			lastLighting = null;
			resizeRenderer();
			panel.refresh();

		}

		metricTimer += delta;
		if ( metricTimer > 0.2 ) {

			metricTimer = 0;
			panel.setLiveMetrics( smoothFrameMs, camera.position.y, atmosphere.parameters.sunElevation );
			panel.setAtmosphereStats( atmosphere.getStats() );
			const pipelineStats = cloudPipeline.getStats();
			document.documentElement.dataset.cloudPass = `${ pipelineStats.cloudWidth }x${ pipelineStats.cloudHeight }`;
			document.documentElement.dataset.scenePass = `${ pipelineStats.sceneWidth }x${ pipelineStats.sceneHeight }`;
			document.documentElement.dataset.cameraWorld = camera.position.toArray().map( Math.round ).join( ',' );
			document.documentElement.dataset.cloudAnchor = clouds.position.toArray().map( Math.round ).join( ',' );

		}

		cloudPipeline.render();

	} );

	window.addEventListener( 'resize', () => {

		camera.aspect = window.innerWidth / window.innerHeight;
		camera.updateProjectionMatrix();
		resizeRenderer();

	} );

	document.querySelector( '.loading-screen' ).classList.add( 'is-done' );
	window.__CYBORG_CLOUDS__ = { renderer, scene, cloudScene, camera, atmosphere, clouds, cloudShadows, cloudPipeline, controls, panel, adaptive };
	markStage( 'render-loop-started' );

}

start().catch( showError );
